import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bell,
  CheckCircle2,
  Clock3,
  Mail,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react'
import { supabase } from '../services/supabase'

type NotificacaoStatus = 'enviado' | 'falha' | 'pendente' | string

interface NotificacaoRow {
  id: string
  user_id: string | null
  assinatura_id: string | null
  tipo: string
  canal: string
  destinatario: string | null
  assunto: string | null
  payload: {
    planoNome?: string
    valor?: number
    dataProximoPagamento?: string
    metodo?: string
    nomeUsuario?: string
    motivo?: string
  } | null
  status: NotificacaoStatus
  provider_id: string | null
  erro: string | null
  created_at: string
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('pt-BR')
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('pt-BR')
}

function formatCurrency(value: number | null | undefined) {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function tipoLabel(tipo: string) {
  if (tipo === 'assinatura_ativada') return 'Assinatura ativada'
  if (tipo === 'pagamento_confirmado') return 'Pagamento confirmado'
  return tipo
}

function StatusBadge({ status }: { status: NotificacaoStatus }) {
  if (status === 'enviado') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Enviado
      </span>
    )
  }
  if (status === 'falha') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
        <XCircle className="h-3.5 w-3.5" />
        Falha
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
      <Clock3 className="h-3.5 w-3.5" />
      {status || 'Pendente'}
    </span>
  )
}

export default function AdminNotificacoes() {
  const [rows, setRows] = useState<NotificacaoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'enviado' | 'falha' | 'pendente'>('todos')
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'assinatura_ativada' | 'pagamento_confirmado'>('todos')

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const { data, error: qError } = await supabase
        .from('notificacoes_enviadas')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200)

      if (qError) throw qError
      setRows((data || []) as NotificacaoRow[])
    } catch (err: any) {
      console.error(err)
      setError(
        err?.message?.includes('does not exist') || err?.code === '42P01'
          ? 'Tabela notificacoes_enviadas não existe. Execute sql/criar_notificacoes_enviadas.sql no Supabase.'
          : err?.message || 'Erro ao carregar notificações'
      )
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const stats = useMemo(() => {
    const total = rows.length
    const enviados = rows.filter((r) => r.status === 'enviado').length
    const falhas = rows.filter((r) => r.status === 'falha').length
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const hojeCount = rows.filter((r) => new Date(r.created_at) >= hoje).length
    return { total, enviados, falhas, hojeCount }
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (filtroStatus !== 'todos' && r.status !== filtroStatus) return false
      if (filtroTipo !== 'todos' && r.tipo !== filtroTipo) return false
      if (!q) return true
      const nome = r.payload?.nomeUsuario || ''
      const plano = r.payload?.planoNome || ''
      return (
        (r.destinatario || '').toLowerCase().includes(q) ||
        nome.toLowerCase().includes(q) ||
        plano.toLowerCase().includes(q) ||
        (r.assunto || '').toLowerCase().includes(q) ||
        (r.assinatura_id || '').toLowerCase().includes(q)
      )
    })
  }, [rows, search, filtroStatus, filtroTipo])

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Bell className="h-6 w-6 text-blue-600" />
            Notificações
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Histórico de e-mails enviados na ativação de assinaturas.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-xs text-gray-500">Total (últimos 200)</div>
          <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-xs text-gray-500">Enviados</div>
          <div className="mt-1 text-2xl font-bold text-emerald-600">{stats.enviados}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-xs text-gray-500">Falhas</div>
          <div className="mt-1 text-2xl font-bold text-red-600">{stats.falhas}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-xs text-gray-500">Hoje</div>
          <div className="mt-1 text-2xl font-bold text-blue-600">{stats.hojeCount}</div>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por e-mail, nome, plano..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <select
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value as typeof filtroStatus)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="todos">Todos os status</option>
          <option value="enviado">Enviado</option>
          <option value="falha">Falha</option>
          <option value="pendente">Pendente</option>
        </select>
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value as typeof filtroTipo)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="todos">Todos os tipos</option>
          <option value="assinatura_ativada">Assinatura ativada</option>
          <option value="pagamento_confirmado">Pagamento confirmado</option>
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quando
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Usuário
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Notificação
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Plano / vencimento
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                    Nenhuma notificação encontrada.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="align-top hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {row.payload?.nomeUsuario || '—'}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                        <Mail className="h-3.5 w-3.5" />
                        {row.destinatario || 'sem e-mail'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {tipoLabel(row.tipo)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {row.assunto || '—'}
                      </div>
                      {row.erro && (
                        <div className="mt-1 text-xs text-red-600 max-w-xs">
                          {row.erro}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>{row.payload?.planoNome || '—'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatCurrency(row.payload?.valor)} · venc. {formatDate(row.payload?.dataProximoPagamento)}
                      </div>
                      {row.payload?.metodo && (
                        <div className="text-xs text-gray-500 mt-0.5 capitalize">
                          {row.payload.metodo === 'cartao' ? 'Cartão' : 'PIX'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
        <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">Próximos passos possíveis nesta página</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Reenviar e-mail em caso de falha</li>
          <li>Avisos de renovação / falha de cobrança</li>
          <li>Cancelamento via app do banco</li>
        </ul>
      </div>
    </div>
  )
}
